/**
 * Saúde dos Anúncios (antes "Validador de Anúncios") — pedidos do Felipe de 25/09/2026 e 15/10/2026.
 *
 *  - Abas: Visão geral (placar por loja + instruções mais repetidas), Qualidade (nota/nível e instruções do próprio
 *    marketplace) e Divergências (título × ficha × descrição, igual ao Validador de antes, agora em todas as lojas).
 *  - Filtros comuns: SKU (inclui variação), título, ID, canal e loja — mantidos ao trocar de aba.
 *  - Estados: o clique só põe em Aguardando; quem confirma é a análise (madrugada ou "Conferir agora").
 *
 * Mockup aprovado: https://claude.ai/artifact/FQx7UpSJtaoHHXoHig3sAk
 * Cálculo em ./validadorAnuncios/logic.ts; regra de negócio no services/validador-anuncios.
 */
import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Grid, Link, Stack, Tab, Tabs,
  Table, TableBody, TableCell, TableHead, TableRow, Typography, alpha, useTheme,
} from '@mui/material'
import { pedirAnaliseProposta } from '../api/bridge'
import { ChartEmpty } from '../components/ChartEmpty'
import { DomainPageShell } from '../components/DomainPageShell'
import { PropostaIaCard, type PropostaIaState } from '../components/PropostaIaCard'
import { useBiAuth } from '../state/BiAuthContext'
import {
  ABAS, CAMPOS, CAMPO_LABEL, DIMENSAO_LABEL, FILTROS_VAZIOS, FILTRO_ANUNCIO_VAZIO, SEVERIDADES, SEVERIDADE_COR, SEVERIDADE_LABEL,
  STATUS_ANUNCIO, aplicaFiltros, chaveAnuncio, contaFiltroAnuncio, contaFiltros, contaPorEstado, contaPorSeveridade, dimensoesPresentes,
  divergenciasOrdenadas, partesDivergencia, pendenciasVisiveis, rotuloIa, temAlerta, topInstrucoes, type Aba, type FiltroAnuncio, type Filtros,
} from './validadorAnuncios/logic'
import type { AnuncioQualidade, AnuncioValidado, Divergencia, ValidadorDados } from './validadorAnuncios/types'
import {
  conferirAnuncio, dispararAnalise, fetchValidadorDados, fetchValidadorStatus, marcarAnuncio, marcarQualidade,
} from './validadorAnuncios/validadorService'
import { AbaQualidade } from './validadorAnuncios/AbaQualidade'
import { AbaVisaoGeral } from './validadorAnuncios/AbaVisaoGeral'
import { FiltroAnuncioBloco } from './validadorAnuncios/FiltroAnuncioBloco'

const nf = (n: number | null | undefined): string => (n == null ? '—' : n.toLocaleString('pt-BR'))
const quando = (s?: string | null) =>
  s ? new Date(s).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—'
const PAGE_SIZE = 60

/** Bloco de filtros com título numerado — mesmo desenho da tela Score. */
function Bloco({ titulo, children, md }: { titulo: string; children: ReactNode; md: number }) {
  return (
    <Grid size={{ xs: 12, md }}>
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 1.25, height: '100%' }}>
        <Typography sx={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '.06em', color: 'text.disabled', mb: 1 }}>{titulo}</Typography>
        <Stack spacing={1}>{children}</Stack>
      </Box>
    </Grid>
  )
}

function ResumoDivergencia({ d }: { d: Divergencia }) {
  const partes = partesDivergencia(d)
  return (
    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap" useFlexGap sx={{ fontSize: 13, lineHeight: 1.45 }}>
      <Chip size="small" variant="outlined" color={SEVERIDADE_COR[d.severidade]} label={DIMENSAO_LABEL[d.dimensao] || d.dimensao} sx={{ height: 20, fontSize: 10.5 }} />
      {partes.map((p, i) => (
        <Fragment key={i}>
          {i > 0 ? <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600 }} aria-label="diferente de">≠</Box> : null}
          <Box component="span" sx={p.erro ? { color: 'error.main', fontWeight: 700 } : undefined}>{p.texto}</Box>
        </Fragment>
      ))}
    </Stack>
  )
}

function Detalhe({
  a, ocupado, onConferir, onMarcar,
}: {
  a: AnuncioValidado
  ocupado: boolean
  onConferir: () => void
  onMarcar: (acao: 'corrigido' | 'falso' | 'desfazer') => void
}) {
  const theme = useTheme()
  const grade = { display: 'grid', gridTemplateColumns: '140px repeat(3, minmax(0, 1fr))', gap: 1.25 }
  return (
    <Stack spacing={1.25} sx={{ py: 1.5 }}>
      <Box sx={{ ...grade, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.secondary' }}>
        <span>Divergência</span>
        {CAMPOS.map(([k]) => <span key={k}>{CAMPO_LABEL[k]}</span>)}
      </Box>
      {divergenciasOrdenadas(a).map((d, i) => (
        <Box key={i}>
          <Box sx={grade}>
            <Stack spacing={0.5} sx={{ pt: 1 }}>
              <Typography variant="body2" fontWeight={600}>{DIMENSAO_LABEL[d.dimensao] || d.dimensao}</Typography>
              <Chip size="small" variant="outlined" color={SEVERIDADE_COR[d.severidade]} label={SEVERIDADE_LABEL[d.severidade]} sx={{ alignSelf: 'flex-start', height: 20, fontSize: 10.5 }} />
              <Typography variant="caption" color="text.secondary">por {d.origem.startsWith('IA') ? d.origem : 'regra automática'}</Typography>
            </Stack>
            {CAMPOS.map(([k]) => {
              const c = d.campos[k]
              const erro = d.provavel_erro === k
              return (
                <Box
                  key={k}
                  sx={{
                    p: 1.25, borderRadius: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75,
                    border: erro ? 2 : 1,
                    borderStyle: c ? 'solid' : 'dashed',
                    borderColor: erro ? 'error.main' : 'divider',
                    bgcolor: erro ? alpha(theme.palette.error.main, 0.04) : c ? 'background.paper' : 'transparent',
                  }}
                >
                  {erro ? <Chip size="small" color="error" label="Provável erro · corrigir aqui" sx={{ alignSelf: 'flex-start' }} /> : null}
                  {c ? (
                    <>
                      <Typography sx={{ fontFamily: '"Outfit", sans-serif', fontWeight: 600, fontSize: 18 }}>{c.valor}</Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>“{c.trecho || 'campo em branco no anúncio'}”</Typography>
                    </>
                  ) : (
                    <Typography variant="caption" color="text.secondary">não menciona</Typography>
                  )}
                </Box>
              )
            })}
          </Box>
          {d.explicacao && d.dimensao !== 'sem descrição' ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', pl: '152px', mt: 0.5 }}>{d.explicacao}</Typography>
          ) : null}
        </Box>
      ))}
      {a.nota ? (
        <Alert severity={a.estado === 'aguardando' ? 'info' : 'warning'} variant="outlined" sx={{ py: 0 }}>{a.nota}</Alert>
      ) : null}
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
        {a.estado === 'pendente' || a.estado === 'aguardando' ? (
          <Button onClick={onConferir} disabled={ocupado} startIcon={ocupado ? <CircularProgress size={14} /> : undefined} sx={{ mr: 'auto' }}>
            Conferir agora
          </Button>
        ) : null}
        {a.estado === 'pendente' ? (
          <>
            <Button variant="outlined" disabled={ocupado} onClick={() => onMarcar('falso')}>Falso positivo</Button>
            <Button variant="contained" disabled={ocupado} onClick={() => onMarcar('corrigido')}>Marquei como corrigido</Button>
          </>
        ) : null}
        {a.estado === 'aguardando' || a.estado === 'falso' ? (
          <Button variant="outlined" disabled={ocupado} onClick={() => onMarcar('desfazer')}>Desfazer</Button>
        ) : null}
      </Stack>
    </Stack>
  )
}

export function ValidadorAnunciosPage() {
  const { user } = useBiAuth()
  const por = user?.nome || user?.email || 'painel'
  const [dados, setDados] = useState<ValidadorDados | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [semAnalise, setSemAnalise] = useState(false)
  const [aba, setAba] = useState<Aba>('pendente')
  const [filtros, setFiltros] = useState<Filtros>(FILTROS_VAZIOS)
  const [secao, setSecao] = useState<'visao' | 'qualidade' | 'divergencias'>('visao')
  const [filtroAnuncio, setFiltroAnuncio] = useState<FiltroAnuncio>(FILTRO_ANUNCIO_VAZIO)
  const [comercial, setComercial] = useState(false)
  const [tipoQ, setTipoQ] = useState('')
  const [aberto, setAberto] = useState<string | null>(null)
  const [ocupado, setOcupado] = useState<string | null>(null)
  const [visivel, setVisivel] = useState(PAGE_SIZE)
  const [rodando, setRodando] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [erroAcao, setErroAcao] = useState<string | null>(null)
  const [leituraIa, setLeituraIa] = useState<PropostaIaState>({ loading: false })

  const load = () => {
    setLoading(true)
    setError(null)
    fetchValidadorDados()
      .then((d) => {
        setDados(d)
        setSemAnalise(false)
        setRodando(Boolean(d.meta.rodando))
      })
      .catch((err: unknown) => {
        const status = (err as { status?: number }).status
        if (status === 503) setSemAnalise(true)
        else setError(err instanceof Error ? err.message : 'Falha ao carregar o Validador')
      })
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  // acompanha a análise completa enquanto houver uma rodando
  useEffect(() => {
    if (!rodando) return
    let cancelado = false
    const timer = window.setInterval(async () => {
      try {
        const st = await fetchValidadorStatus()
        if (cancelado || st.rodando) return
        setRodando(false)
        setMsg(st.erro ? null : 'Análise concluída — atualizando a lista…')
        if (st.erro) setErroAcao(`A análise falhou: ${st.erro}`)
        load()
      } catch {
        /* tenta de novo no próximo ciclo */
      }
    }, 15_000)
    return () => { cancelado = true; window.clearInterval(timer) }
  }, [rodando])

  const atualizarAgora = async () => {
    setErroAcao(null)
    try {
      const r = await dispararAnalise()
      setRodando(true)
      setMsg(r.mensagem || 'Análise disparada — todas as lojas, costuma levar 30 a 60 minutos.')
    } catch (err: unknown) {
      setErroAcao(err instanceof Error ? err.message : 'Falha ao disparar a análise')
    }
  }

  const agir = async (id: string, fn: () => Promise<unknown>) => {
    setErroAcao(null)
    setOcupado(id)
    try {
      const r = await fn()
      const d = await fetchValidadorDados()
      setDados(d)
      const eq = (r as { erro_qualidade?: string | null } | null | undefined)?.erro_qualidade
      if (eq) setErroAcao(`Divergências conferidas; a qualidade não foi lida agora: ${eq}`)
    } catch (err: unknown) {
      setErroAcao(err instanceof Error ? err.message : 'Falha ao salvar')
    } finally {
      setOcupado(null)
    }
  }

  const anuncios = useMemo(() => dados?.anuncios ?? [], [dados])
  const corrigidos = dados?.corrigidos ?? []
  const porEstado = useMemo(() => contaPorEstado(anuncios, corrigidos.length), [anuncios, corrigidos.length])
  const porSev = useMemo(() => contaPorSeveridade(anuncios), [anuncios])
  const dims = useMemo(() => dimensoesPresentes(anuncios), [anuncios])
  const lista = useMemo(() => (aba === 'corrigido' ? [] : aplicaFiltros(anuncios, aba, filtros, filtroAnuncio)), [anuncios, aba, filtros, filtroAnuncio])
  const qualidade = useMemo(() => dados?.qualidade ?? [], [dados])
  const comMelhoria = qualidade.filter((a: AnuncioQualidade) => a.estado !== 'ignorado' && pendenciasVisiveis(a, comercial).length).length
  const comAlerta = qualidade.filter((a: AnuncioQualidade) => a.estado !== 'ignorado' && temAlerta(a, comercial)).length
  const notasMl = (dados?.resumo_lojas ?? []).filter((r) => r.nota_media != null)
  const notaMediaMl = notasMl.length ? Math.round(notasMl.reduce((s, r) => s + (r.nota_media || 0) * r.lidos, 0) / Math.max(1, notasMl.reduce((s, r) => s + r.lidos, 0))) : null
  const nFiltros = contaFiltros(filtros)
  useEffect(() => { setVisivel(PAGE_SIZE) }, [aba, filtros, filtroAnuncio])
  const meta = dados?.meta
  const stats = meta?.stats
  const setF = <K extends keyof Filtros>(k: K, v: Filtros[K]) => setFiltros((f) => ({ ...f, [k]: f[k] === v ? FILTROS_VAZIOS[k] : v }))

  const carregarLeitura = useCallback(async () => {
    if (!dados) return
    const pend = porEstado.pendente + porEstado.aguardando
    const top = anuncios.slice(0, 8).map((a) => ({
      id: a.id,
      severidade: a.severidade_max,
      n_divergencias: a.n_divergencias,
      vendas: a.vendas,
      resumo: a.resumo,
    }))
    const fallback =
      `${pend} anúncios com divergência aberta · alta ${porSev.alta} · média ${porSev['média'] || 0}` +
      (top[0] ? ` · exemplo: ${top[0].id} (${top[0].severidade})` : '')
    setLeituraIa((prev) => ({ ...prev, loading: true, erro: null }))
    const data = await pedirAnaliseProposta({
      tipo: 'insight_reflexao',
      insight: {
        dominio: 'validador_anuncios',
        dominio_label: 'Saúde dos Anúncios',
        titulo: 'Leitura da qualidade e das divergências dos anúncios',
        leitura: fallback,
        periodo: meta?.gerado_em || null,
        metricas: {
          loja: meta?.loja,
          analisados: stats?.analisados ?? null,
          com_divergencia: pend,
          severidade_alta: porSev.alta,
          severidade_media: porSev['média'] || 0,
          aguardando: porEstado.aguardando,
          corrigidos: porEstado.corrigido,
          usa_ia: Boolean(meta?.usa_ia),
          ia: meta?.ia ?? null,
          lojas: (dados.resumo_lojas ?? []).map((r) => ({ loja: r.rotulo, lidos: r.lidos, nota_media: r.nota_media, com_pendencia: r.com_pendencia, com_alerta: r.com_alerta, com_divergencia: r.com_divergencia })),
          instrucoes_top: topInstrucoes(dados.qualidade ?? [], false).slice(0, 5).map((t) => ({ texto: t.texto, n: t.n })),
          top,
        },
      },
    })
    if (!data.ok) {
      setLeituraIa({ loading: false, texto: fallback, fonte: 'regras', modelo: null, erro: null })
      return
    }
    setLeituraIa({
      loading: false,
      texto: data.leitura || data.texto || fallback,
      modelo: data.modelo,
      fonte: data.fonte,
      cached: data.cached,
    })
  }, [dados, anuncios, porEstado, porSev, meta, stats])

  useEffect(() => {
    if (dados) void carregarLeitura()
  }, [dados?.meta?.gerado_em, carregarLeitura]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <DomainPageShell
      title="Saúde dos Anúncios"
      subtitle={meta?.gerado_em
        ? `Analisado em ${quando(meta.gerado_em)} · ${nf((dados?.resumo_lojas ?? []).reduce((s, r) => s + r.lidos, 0))} anúncios em ${nf((meta.lojas ?? []).length)} lojas · nota e instruções do próprio marketplace + divergências entre título, ficha técnica e descrição`
        : 'Nota e instruções do próprio marketplace + divergências entre título, ficha técnica e descrição'}
      loading={loading}
      error={error}
      onRetry={load}
      stats={dados ? [
        { label: 'Anúncios com o que melhorar', value: nf(comMelhoria), hint: 'instrução do próprio marketplace' },
        { label: 'Com alerta do marketplace', value: nf(comAlerta), hint: 'rebaixado, suprimido, não comprável, violação' },
        { label: 'Nota média (ML)', value: notaMediaMl == null ? '—' : String(notaMediaMl), hint: 'só o ML dá nota 0–100' },
        { label: 'Anúncios com divergência', value: nf(porEstado.pendente + porEstado.aguardando), hint: 'título × ficha × descrição' },
      ] : undefined}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
          {meta ? (
            <Typography variant="body2">
              <b>{nf((meta.canais_status ?? []).filter((c) => c.ok).length)} de {nf((meta.canais_status ?? []).length)} lojas lidas</b>
              {(meta.canais_status ?? []).filter((c) => !c.ok).map((c) => ` · ${c.loja}: sem leitura`).join('')}
              {meta.usa_ia ? ` · ${nf(stats?.com_pista)} casos revisados por IA (${rotuloIa(meta.ia) || 'IA'})` : ' · só regras (IA desligada)'}
              {stats?.ia_pausada ? ' · IA pausada (teto do mês): o resto fica para a próxima análise' : ''}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary">
            Análise automática às {meta?.proxima_analise || '04:30'} (São Paulo) · só reanalisa anúncio editado
          </Typography>
        </Stack>
        <Button variant="contained" disabled={rodando || loading} startIcon={rodando ? <CircularProgress size={14} color="inherit" /> : undefined} onClick={() => void atualizarAgora()} sx={{ whiteSpace: 'nowrap' }}>
          {rodando ? 'Analisando…' : 'Atualizar agora'}
        </Button>
      </Stack>
      {erroAcao ? <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErroAcao(null)}>{erroAcao}</Alert> : null}
      {msg ? <Alert severity="info" sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg}</Alert> : null}
      {semAnalise && !rodando ? (
        <ChartEmpty title="Ainda não existe análise." hint="Clique em Atualizar agora para rodar a primeira (todas as lojas, 30 a 60 minutos)." suggestPeriod={false} />
      ) : null}

      {dados ? (
        <>
          <Card sx={{ mb: 2 }}>
            <Tabs value={secao} onChange={(_, v: typeof secao) => setSecao(v)} variant="scrollable" allowScrollButtonsMobile sx={{ px: 1 }}>
              <Tab value="visao" label="Visão geral" />
              <Tab value="qualidade" label={`Qualidade (${nf(comMelhoria)})`} />
              <Tab value="divergencias" label={`Divergências (${nf(porEstado.pendente + porEstado.aguardando)})`} />
            </Tabs>
          </Card>
          {secao === 'visao' ? (
            <Card sx={{ mb: 2 }}><CardContent>
              <AbaVisaoGeral dados={dados} comercial={comercial} onComercial={setComercial}
                onVerLoja={(chave) => { setFiltroAnuncio({ ...FILTRO_ANUNCIO_VAZIO, loja: chave }); setSecao('qualidade') }}
                onVerInstrucao={(t) => { setTipoQ(t); setSecao('qualidade') }} />
            </CardContent></Card>
          ) : null}
          {secao === 'qualidade' ? (
            <Card sx={{ mb: 2 }}><CardContent>
              <AbaQualidade dados={dados} filtro={filtroAnuncio} onFiltro={setFiltroAnuncio} comercial={comercial} onComercial={setComercial}
                tipo={tipoQ} onTipo={setTipoQ} ocupado={ocupado}
                onConferir={(a) => void agir(a.uid, () => conferirAnuncio(a.id, a.loja_key))}
                onMarcar={(a, acao) => void agir(a.uid, () => marcarQualidade(a.id, a.loja_key, acao, por))}
                onIrDivergencia={(a) => { setFiltroAnuncio({ ...FILTRO_ANUNCIO_VAZIO, id: a.id, loja: a.loja_key }); setSecao('divergencias') }} />
            </CardContent></Card>
          ) : null}
          {secao === 'divergencias' ? (
            <>
            <Card sx={{ mb: 2 }}>
              <Tabs value={aba} onChange={(_, v: Aba) => setAba(v)} variant="scrollable" allowScrollButtonsMobile sx={{ px: 1 }}>
                {ABAS.map((t) => <Tab key={t.id} value={t.id} label={`${t.label} (${nf(porEstado[t.id])})`} />)}
              </Tabs>
            </Card>

            <Card>
              <CardContent>
                {aba === 'corrigido' ? (
                  <>
                    <Typography variant="h6" sx={{ fontSize: 16, mb: 1.5 }}>Corrigidos confirmados pela análise</Typography>
                    {corrigidos.length === 0 ? (
                      <ChartEmpty title="Nenhum anúncio corrigido ainda." hint="Quando a análise vê que a divergência sumiu, o anúncio aparece aqui." suggestPeriod={false} dense />
                    ) : (
                      <Box sx={{ width: '100%', overflowX: 'auto' }}>
                        <Table size="small" sx={{ '& th': { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.secondary', bgcolor: 'action.hover' } }}>
                          <TableHead>
                            <TableRow><TableCell>Anúncio</TableCell><TableCell>O que estava divergente</TableCell><TableCell>Confirmação</TableCell></TableRow>
                          </TableHead>
                          <TableBody>
                            {corrigidos.map((c) => (
                              <TableRow key={c.uid || c.id} hover>
                                <TableCell sx={{ maxWidth: 320 }}>
                                  {c.link ? <Link href={c.link} target="_blank" rel="noreferrer" underline="hover" noWrap sx={{ display: 'block', fontWeight: 600 }}>{c.titulo || c.id}</Link> : <Typography variant="body2" fontWeight={600}>{c.titulo || c.id}</Typography>}
                                  <Typography variant="caption" color="text.secondary">{c.id}{c.sku ? ` · SKU ${c.sku}` : ''} · {c.loja || ''}</Typography>
                                </TableCell>
                                <TableCell><Typography variant="body2">{c.resumo || '—'}</Typography></TableCell>
                                <TableCell><Typography variant="caption" color="success.main">{c.nota}</Typography></TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                      <Typography variant="h6" sx={{ fontSize: 16, mr: 'auto' }}>Anúncios com divergência</Typography>
                      <Typography variant="caption" color="text.secondary">{nf(lista.length)} de {nf(porEstado[aba])} anúncios</Typography>
                      {nFiltros + contaFiltroAnuncio(filtroAnuncio) ? <Button size="small" variant="outlined" onClick={() => { setFiltros(FILTROS_VAZIOS); setFiltroAnuncio(FILTRO_ANUNCIO_VAZIO) }}>Limpar tudo ({nFiltros + contaFiltroAnuncio(filtroAnuncio)})</Button> : null}
                    </Stack>

                    <Grid container spacing={1.25} sx={{ mb: 1.5 }}>
                      <FiltroAnuncioBloco valor={filtroAnuncio} onChange={setFiltroAnuncio} lojas={meta?.lojas || []} md={6} />
                      <Bloco titulo="2 · Gravidade" md={2.5}>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {SEVERIDADES.map((s) => (
                            <Chip key={s} size="small" label={`${SEVERIDADE_LABEL[s]} (${nf(anuncios.filter((a) => a.divergencias.some((d) => d.severidade === s)).length)})`}
                              variant={filtros.severidade === s ? 'filled' : 'outlined'} color={filtros.severidade === s ? 'primary' : 'default'} onClick={() => setF('severidade', s)} />
                          ))}
                        </Stack>
                      </Bloco>
                      <Bloco titulo="3 · O que diverge" md={3.5}>
                        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                          {dims.map((d) => (
                            <Chip key={d.id} size="small" label={`${DIMENSAO_LABEL[d.id] || d.id} (${nf(d.n)})`}
                              variant={filtros.dimensao === d.id ? 'filled' : 'outlined'} color={filtros.dimensao === d.id ? 'primary' : 'default'} onClick={() => setF('dimensao', d.id)} />
                          ))}
                        </Stack>
                      </Bloco>
                    </Grid>

                    {lista.length === 0 ? (
                      <ChartEmpty title="Nenhum anúncio nesta aba com esses filtros." hint="Troque a aba ou limpe os filtros." suggestPeriod={false} dense />
                    ) : (
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          Ordem: severidade → nº de divergências → vendas · o valor em vermelho é o que discorda dos outros campos
                        </Typography>
                        <Box sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                          <Table size="small" sx={{ minWidth: 980, '& th': { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: 'text.secondary', bgcolor: 'action.hover' } }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ width: 96 }}>Severidade</TableCell>
                                <TableCell>Anúncio</TableCell>
                                <TableCell>O que validar</TableCell>
                                <TableCell align="right" sx={{ width: 80 }}>Vendas</TableCell>
                                <TableCell align="right" sx={{ width: 170 }}>Ações</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {lista.slice(0, visivel).map((a) => {
                                const open = aberto === chaveAnuncio(a)
                                return (
                                  <Fragment key={chaveAnuncio(a)}>
                                    <TableRow hover selected={open} sx={{ verticalAlign: 'top', '& > td': open ? { borderBottom: 0 } : undefined }}>
                                      <TableCell>
                                        <Chip size="small" variant="outlined" color={SEVERIDADE_COR[a.severidade_max]} label={SEVERIDADE_LABEL[a.severidade_max]} />
                                      </TableCell>
                                      <TableCell sx={{ maxWidth: 320 }}>
                                        <Link href={a.link} target="_blank" rel="noreferrer" underline="hover" noWrap sx={{ display: 'block', fontWeight: 600 }}>{a.titulo}</Link>
                                        <Typography variant="caption" color="text.secondary">
                                          {a.id} · SKU {a.sku || '—'} · {a.loja} · {STATUS_ANUNCIO[a.status] || a.status}
                                        </Typography>
                                      </TableCell>
                                      <TableCell>
                                        <Stack spacing={0.5}>
                                          {divergenciasOrdenadas(a).map((d, i) => <ResumoDivergencia key={i} d={d} />)}
                                        </Stack>
                                      </TableCell>
                                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{nf(a.vendas)}</TableCell>
                                      <TableCell align="right">
                                        <Stack spacing={0.5} alignItems="flex-end">
                                          <Button size="small" variant="outlined" href={a.link} target="_blank" rel="noreferrer" sx={{ whiteSpace: 'nowrap' }}>Abrir anúncio ↗</Button>
                                          <Button size="small" aria-expanded={open} onClick={() => setAberto(open ? null : chaveAnuncio(a))}>{open ? 'Fechar ▴' : 'Detalhes ▾'}</Button>
                                        </Stack>
                                      </TableCell>
                                    </TableRow>
                                    {open ? (
                                      <TableRow selected>
                                        <TableCell colSpan={5}>
                                          <Detalhe
                                            a={a}
                                            ocupado={ocupado === chaveAnuncio(a)}
                                            onConferir={() => void agir(chaveAnuncio(a), () => conferirAnuncio(a.id, a.loja_key || '777058595'))}
                                            onMarcar={(acao) => void agir(chaveAnuncio(a), () => marcarAnuncio(a.id, a.loja_key || '777058595', acao, por))}
                                          />
                                        </TableCell>
                                      </TableRow>
                                    ) : null}
                                  </Fragment>
                                )
                              })}
                            </TableBody>
                          </Table>
                        </Box>
                        {visivel < lista.length ? (
                          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1.5 }}>
                            <Button variant="outlined" onClick={() => setVisivel((v) => v + PAGE_SIZE)}>Carregar mais ({nf(lista.length - visivel)} restantes)</Button>
                          </Box>
                        ) : null}
                      </>
                    )}
                  </>
                )}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  Corrigiu direto no marketplace? A análise da madrugada confirma sozinha. "Marquei como corrigido" põe em Aguardando confirmação;
                  se a divergência continuar, o anúncio volta para Pendentes com o aviso. Falso positivo não volta, a menos que o anúncio mude.
                </Typography>
              </CardContent>
            </Card>

            </>
          ) : null}

          <Box sx={{ mt: 2 }}>
            <PropostaIaCard
              title="Leitura da Saúde dos Anúncios"
              caption="Verdade = lista e severidades da análise (regras + Claude Haiku nos casos duvidosos). Proposta = leitura Claude — revise antes de priorizar correções."
              state={leituraIa}
              onRetry={() => void carregarLeitura()}
            />
          </Box>
        </>
      ) : null}
    </DomainPageShell>
  )
}
